import * as angular from 'angular';
import * as _ from 'lodash';

import { IGroupService, ICurrentApplicationService, IApprovalStrategyService, ITemplateService, IFrequencyFormatHelperService } from '../../services';
import { guidEmpty } from '../../common/constants';
import { INamedGuid, Template, GroupTreeViewModel, ScriptType, Group, TreeType, IApprovalStrategy, GroupStepsAvailability } from '../../models/index';
import { configModes } from '../../common/ConfigModes';

export interface IModelStructureMenu {
    model: {
        property: string;
        isVisible: boolean;
    };
    label: string;
    width: number;
}

export interface IModelStructureService {
    getMenu(): IModelStructureMenu[];
    getTree(): angular.IPromise<GroupTreeViewModel[]>;
    buildTree(
        dto: TreeType[],
        templates: INamedGuid[],
        strategies: IApprovalStrategy[],
        allSteps: { [groupGuid: string]: { [index: number]: boolean; }; },
        changesCount: { [guid: string]: number }
    ): GroupTreeViewModel[];
}

export class ModelStructureService implements IModelStructureService {
    static serviceId = 'modelStructureService';

    static $inject = [
        '$q',
        '$translate',
        'groupService',
        'currentApplicationService',
        'approvalStrategyService',
        'templateService',
        'frequencyFormatHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _groupService: IGroupService,
        private _currentApplicationService: ICurrentApplicationService,
        private _approvalStrategyService: IApprovalStrategyService,
        private _templateService: ITemplateService,
        private _frequencyFormatHelper: IFrequencyFormatHelperService
    ) {
    }

    getTree(): angular.IPromise<GroupTreeViewModel[]> {
        return this._$q
            .all([
                this._groupService.getAllAsTree(),
                this._templateService.getAll(),
                this._approvalStrategyService.getAll(),
                this._groupService.getSteps(),
                this._templateService.countAllChanges()
            ])
            .then(result => {
                const dto = result[0];
                const strategies = result[2];
                const allSteps = result[3];
                return this.buildTree(dto, result[1], strategies, allSteps, result[4]);
            });
    }

    buildTree(
        dto: TreeType[],
        rawTemplates: Template[],
        strategies: IApprovalStrategy[],
        allSteps: { [groupGuid: string]: { [index: number]: boolean; }; },
        changesCount: { [guid: string]: number }
    ) {
        const templates = this.flatTemplateList(rawTemplates, '');
        const groups = (dto as GroupTreeViewModel[]).map((d: GroupTreeViewModel) => {
            try {
                d.displayFrequency = (d.frequency && d.frequency.interval > 0)
                    ? this._frequencyFormatHelper.formatFrequency(d.frequency)
                    : '';
                const templateGuid = d.templateGuid;
                d.templateName = this.getName(templateGuid, templates as any[]);
                d.approvalStrategyName = this.getName(d.approvalStrategyGuid, strategies as any[]);
                d.validationStrategyName = this._$translate.instant(configModes[d.configMode]);
                const tmp = allSteps[d.guid];
                if (tmp) {
                    const steps: GroupStepsAvailability[] = [];
                    Object.keys(tmp).forEach(key => {
                        steps.push({
                            name: key,
                            available: tmp[key],
                            active: false,
                            isScriptV3: d.scriptType === ScriptType.PythonV3
                        });
                    });
                    d.steps = steps;
                }
                d.changeCount = changesCount[d.guid] || 0;
                return d;
            } catch (e) {
                console.error(e);
                return d;
            }
        });
        return groups;
    }

    getMenu(): IModelStructureMenu[] {
        const menu: IModelStructureMenu[] = [];
        menu.push({
            model: {
                property: 'type',
                isVisible: true
            },
            label: 'Type',
            width: 2
        });
        menu.push({
            model: {
                property: 'shortComment',
                isVisible: true
            },
            label: 'Short comment',
            width: 2
        });
        menu.push({
            model: {
                property: 'longComment',
                isVisible: false
            },
            label: 'Long comment',
            width: 3
        });
        menu.push({
            model: {
                property: 'documentation',
                isVisible: false
            },
            label: 'Documentation',
            width: 3
        });
        menu.push({
            model: {
                property: 'templateName',
                isVisible: false
            },
            label: 'Template',
            width: 3
        });
        menu.push({
            model: {
                property: 'displayFrequency',
                isVisible: false
            },
            label: 'Frequency',
            width: 2
        });
        menu.push({
            model: {
                property: 'validationStrategyName',
                isVisible: false
            },
            label: 'Validation',
            width: 2
        });
        if (this._currentApplicationService.getApplicationConfiguration().validationEnabled) {

            menu.push({
                model: {
                    property: 'approvalStrategyName',
                    isVisible: false
                },
                label: 'Approval strategy',
                width: 2
            });
        }
        menu.push({
            model: {
                property: 'ownFields',
                isVisible: false
            },
            label: 'Own fields',
            width: 3
        });
        menu.push({
            model: {
                property: 'steps',
                isVisible: true
            },
            label: 'Steps',
            width: 0
        });
        return menu;
    }

    private flatTemplateList(templates: Template[], prefix: string): INamedGuid[] {
        let result: INamedGuid[] = [];
        templates.forEach(t => {
            const name = prefix + t.name;
            result.push({ guid: t.guid, name: name });
            if (t.children && t.children.length > 0) {
                result = result.concat(this.flatTemplateList(t.children, name + '/'));
            }
        });
        return result;
    }

    private getName(guid: string, collection: INamedGuid[]): string {
        let name: string = '';
        if (guid && guid !== guidEmpty) {
            const found = _.find(collection, t => t.guid === guid);
            if (found) {
                name = found.name;
            } else {
                name = 'Not found';
            }
        }
        return name;
    }
}